export interface PageTab {
  key: string;
  label: string;
  /**
   * How many records sit behind this tab, rendered as a pill after the label. Optional —
   * omit (or pass null while the count is still loading) and the tab is label-only, which
   * is what the tab strips that predate counts render.
   */
  count?: number | null;
}

export interface PageTabsProps {
  tabs: PageTab[];
  active: string;
  onChange: (key: string) => void;
  className?: string;
}

/**
 * Underlined tab strip for pages that merge two surfaces (User Management,
 * Attendance Config). Scrolls horizontally rather than wrapping on narrow screens.
 */
export default function PageTabs({ tabs, active, onChange, className = '' }: PageTabsProps) {
  if (tabs.length < 2) return null;
  return (
    <div className={`flex gap-1 border-b border-border overflow-x-auto ${className}`}>
      {tabs.map((t) => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${
              isActive
                ? 'border-brand text-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
            {t.count != null && (
              // `bg-brand-light` rather than `bg-brand/12`: Tailwind v3 drops the opacity
              // modifier on a colour defined as a bare `var()`, which is what --brand is.
              <span
                className={`inline-flex min-w-[1.5rem] justify-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold leading-none tabular-nums ${
                  isActive ? 'bg-brand-light text-brand' : 'bg-muted text-muted-foreground'
                }`}
              >
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
